import React, {
    ReactElement,
    useContext, useEffect, useMemo, useState,
} from "react";
import { Form } from "react-bootstrap";
import { AiOutlineTag } from "react-icons/ai";
import { RiArticleLine } from "react-icons/ri";
import TimeAgo from "react-timeago";
import { Loading } from "../../components";
import Confirm from "../../components/confirm/Confirm";

import { Article, ArticleTag, useGetUpdatesQuery } from "../../__generated__/api";
import { HtmlRenderer } from "../../components/html-render/HtmlRenderer";
import { useTheme } from "../Theme";
import Styles from "./UpdatesContext.module.scss";

import UpdatesModal from "./UpdateModal";
import { LoggingHandler } from "../logging/LoggingContext";

interface UpdatesActions {
    show(): void;
    unreadCount(): number;
}

export const UpdatesContext = React.createContext<UpdatesActions>({
    show() {
        // do nothing
    },
    unreadCount() {
        return 0;
    },
});

interface UpdatesSettings {
    selectedTags: string[];
    lastReadDate: number
}

const KEY = "updates";

export function DisplayUpdates({
    articles, categories, onSave, settings,
}: {
    articles: Article[];
    categories: ArticleTag[];
    onSave: (settings: Partial<UpdatesSettings>) => void;
    settings: UpdatesSettings
}) {
    const theme = useTheme();
    let allUpdates = articles?.sortByDescending(article => new Date(article.publishedDate).getTime());

    if (settings.selectedTags?.isNotEmpty) {
        const set = new Set(settings.selectedTags);
        allUpdates = allUpdates.filter(row => row.tags.some(tag => set.has(tag.id)));
    }
    return (

        <div className={`d-flex h-100 ${Styles.updates}`}>
            <div className="col-3 p-3">
                <h4>
                    Updates
                </h4>
                <small>Here are some of the latest things we have been cooking up.</small>

                <div className="list-group mt-3">
                    <strong>Categories:</strong>
                    {categories.map(tag => (
                        <div className="d-flex mt-2">
                            <div>
                                <Form.Check
                                    type="switch"
                                    checked={settings.selectedTags?.some(selected => tag.id === selected)}
                                    onChange={evt => {
                                        const tags = settings.selectedTags ?? [];
                                        onSave({
                                            selectedTags: evt.target.checked
                                                ? tags.concat([tag.id])
                                                : tags.filter(row => tag.id !== row),
                                        });
                                    }}
                                />
                            </div>
                            <div className="col-1 me-2 text-center">
                                {tag.iconUrl ? (
                                    <img
                                        src={tag.iconUrl as string}
                                        className="w-100 p-1 border-0"
                                        alt={tag.title}
                                    />
                                ) : (
                                    <AiOutlineTag className="fs-4" />
                                )}
                            </div>
                            <div className="col text-truncate">
                                {tag.title}
                            </div>
                        </div>
                    ))}
                </div>

            </div>

            <div className="overflow-auto col shadow p-3">

                <div className="container list-group list-group-flush ">
                    {allUpdates.isEmpty && (
                        <div className="text-center mt-5 text-light">
                            Sorry no new updates matching your filters. Try changing the categories on the left.
                        </div>
                    )}
                    {allUpdates?.map((update, index) => {
                        const publishedDate = new Date(update.publishedDate);
                        const isLast = allUpdates.length - 1 === index;
                        return (

                            <div className="list-group-item border-0 px-2" key={update.id}>
                                <div className="hstack mb-2 ">
                                    <div className="flex-shrink-0 me-3 align-self-start">
                                        {update.iconUrl
                                            ? (
                                                <img
                                                    src={update.iconUrl}
                                                    alt="Update icon"
                                                    className={`
                                                        position-relative
                                                        border-0
                                                        rounded img-thumbnail ${Styles.icon}
                                                    `}
                                                />
                                            )
                                            : (
                                                <RiArticleLine
                                                    className={Styles.icon + (theme.isV3 ? "" : ` text-light-300 fs-1`)}
                                                />
                                            )}
                                    </div>
                                    <div className="flex-grow-1">

                                        <h4 className={theme.isV3 ? Styles.title : "mb-1"}>
                                            {update?.title}
                                        </h4>
                                        <div className={theme.isV3 ? Styles.metadata : "text-light-400"}>
                                            <small>
                                                Published
                                                {" "}
                                                <TimeAgo
                                                    date={publishedDate}
                                                    minPeriod={60}
                                                    title={
                                                        `${publishedDate?.toLocaleDateString()}
                                                                         ${publishedDate?.toLocaleTimeString()}`
                                                    }
                                                />
                                                {" "}
                                                by
                                                {" "}
                                                {update.publishedBy?.name ?? "Automation"}
                                                .
                                            </small>
                                            {" "}
                                            |
                                            {" "}
                                            {update.tags.map(tag => (
                                                <small className="me-2" key={tag.id}>
                                                    #
                                                    {tag.title}
                                                </small>
                                            ))}

                                        </div>

                                    </div>
                                </div>
                                <HtmlRenderer
                                    html={update.body.html as string}
                                    shouldAllowMedia
                                    className={`p-1 w-100 ${isLast ? "" : "border-bottom"} mb-3`}
                                />
                                {/* <Editor
                                    isReadOnly
                                    id={update?.id}
                                    text={update.body.html as string}
                                    isScrollDisabled
                                    className={`p-1 w-100 ${isLast ? "" : "border-bottom"} mb-3`}
                                /> */}
                            </div>
                        );
                    })}
                </div>
            </div>

        </div>

    );
}

export function UpdatesProvider({ children }: {children: ReactElement}): (ReactElement | null) {
    const [isDisplayed, setIsDisplayed] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);

    const [lastRead, setLastRead] = useState(0);

    const { data, loading } = useGetUpdatesQuery();
    const theme = useTheme();

    const instance = useMemo(() => ({
        show: () => setIsDisplayed(true),
        unreadCount: () => unreadCount,
    }), [unreadCount]);

    const handleHide = LoggingHandler.instance.wrapMetric(
        [LoggingHandler.Metrics.UPDATES, "show"],
        () => setIsDisplayed(false),
    );

    const [settings, setSettings] = useState<UpdatesSettings>(() => {
        try {
            let current = localStorage.getItem(KEY);
            if (!current) {
                current = JSON.stringify({
                    lastReadDate: 0,
                });
                localStorage.setItem(KEY, current);
            }
            return JSON.parse(current) as UpdatesSettings;
        } catch (err) {
            const state = {
                lastReadDate: Date.now(),
            };
            localStorage.setItem(KEY, JSON.stringify(state));
            return state as UpdatesSettings;
        }
    });

    useEffect(() => {
        if (settings) {
            localStorage.setItem(KEY, JSON.stringify(settings));
            if (!lastRead) {
                setLastRead(settings.lastReadDate);
            }
        }
    }, [settings, lastRead]);

    useEffect(() => {
        if (isDisplayed) {
            setUnreadCount(0);
            setSettings({
                ...settings,
                lastReadDate: Date.now(),
            });
            LoggingHandler.instance.metric([LoggingHandler.Metrics.UPDATES, "show"]);
        }
    }, [isDisplayed]);

    useEffect(() => {
        let updates = data?.updates;
        if (!updates) {
            return;
        }
        if (settings.selectedTags?.isNotEmpty) {
            const tagSet = new Set(settings.selectedTags ?? []);
            updates = updates?.filter(update => update.tags.some(tag => tagSet.has(tag.id)));
        }

        const lastPublish = settings.lastReadDate ?? Date.now();
        const unread = updates?.count(row => new Date(row.publishedDate).getTime() > lastPublish) ?? 0;
        setUnreadCount(unread);
    }, [data]);

    return (
        <UpdatesContext.Provider value={instance}>

            {isDisplayed && (
                (theme.isV3 ? (
                    <UpdatesModal
                        tags={data?.updateCategories as ArticleTag[]}
                        articles={data?.updates as Article[]}
                        isLoading={loading}
                        onHide={handleHide}
                        setSettings={setSettings}
                        settings={settings}
                        lastRead={lastRead}
                    />
                ) : (
                    <Confirm
                        shouldShow={isDisplayed}
                        onHide={handleHide}
                        closeText="Close"
                        dialogClassName="full-size-container"
                        size="xl"
                        bodyClassName="p-0"
                    >
                        {loading ? <Loading /> : (
                            <DisplayUpdates
                                articles={data?.updates as Article[]}
                                categories={data?.updateCategories as ArticleTag[]}
                                settings={settings}
                                onSave={updates => setSettings({
                                    ...settings,
                                    ...updates,
                                })}
                            />
                        )}
                    </Confirm>
                ))

            )}
            {children}
        </UpdatesContext.Provider>
    );
}

export default function useUpdatesContext(): UpdatesActions {
    return useContext(UpdatesContext) ?? {};
}
